import { z } from "zod";
import { PhaseSchema } from "./phase";

// To sync with BaseDbWorkflow in backend/schemas/workflow.ts
const BaseWorkflowSchema = z.object({
  // Primary fields
  id: z.string(),

  // Version releases
  workflowGroupId: z.string(),
  releaseVersion: z.number().nullable(),
  status: z.string(), // Uses kb_status ENUM: 'draft' | 'released'
  releaseNote: z.string().nullable().optional(),

  // Relations
  agentId: z.string(), // UNIQUE - 1:1 with agents
  creatorId: z.string(),

  // History recording
  historyVersion: z.number(),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  releasedAt: z.string().nullable().optional(),

  // Data fields (top-level columns)
  name: z.string(),
  description: z.string().nullable().optional(),
  createdWithAi: z.boolean().nullable().optional(),

  // metadata // rajouter sql
  metadata: z.object(z.unknown()).optional(), // Generic object type
});

export const WorkflowSchema = BaseWorkflowSchema;

export const WorkflowListItemSchema = BaseWorkflowSchema.extend({
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  agentName: z.string().nullable().optional(),
  agentStatus: z.string().nullable().optional(),
  workspaceId: z.string().nullable().optional(),
});

export const WorkflowListSchema = z.array(WorkflowListItemSchema);

// Create workflow API payload (camelCase)
export const CreateWorkflowApiPayloadSchema = z.object({
  agentId: z.string().uuid("Invalid agent ID"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  createdWithAi: z.boolean().optional(),
});

// Update workflow API payload (camelCase, partial)
// historyVersion is required for optimistic locking
export const UpdateWorkflowApiPayloadSchema = z.object({
  historyVersion: z.number().int().positive(),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
});

export const WorkflowWithPhasesSchema = WorkflowSchema.extend({
  phases: z.array(PhaseSchema),
});

export type CreateWorkflowApiPayload = z.infer<typeof CreateWorkflowApiPayloadSchema>;
export type CreateWorkflowApiResponse = Workflow;

export type UpdateWorkflowApiPayload = z.infer<typeof UpdateWorkflowApiPayloadSchema>;
export type UpdateWorkflowApiResponse = Workflow;

export type WorkflowListApiResponse = z.infer<typeof WorkflowListSchema>;

export type Workflow = z.infer<typeof WorkflowSchema>;
export type WorkflowListItem = z.infer<typeof WorkflowListItemSchema>;

export type WorkflowWithPhases = z.infer<typeof WorkflowWithPhasesSchema>;
